import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  UploadSimple, Image, VideoCamera, Microphone, FileText, Trash,
  DownloadSimple, MagnifyingGlass, X, Camera, Play, Eye
} from '@phosphor-icons/react';
import { Dialog, DialogContent } from './ui/dialog';

const API = '/api';
const CHUNK_SIZE = 10 * 1024 * 1024;

const CATEGORY_ICONS = { image: Image, video: VideoCamera, audio: Microphone, document: FileText };
const CATEGORY_COLORS = { image: 'bg-purple-100 text-purple-700', video: 'bg-red-100 text-red-700', audio: 'bg-amber-100 text-amber-700', document: 'bg-blue-100 text-blue-700' };

const DOC_TAGS = [
  'Wreckage Overview', 'Close-up Detail', 'Cockpit/Bridge', 'Ground Scars',
  'Component Position', 'Structural Damage', 'Environmental', 'Witness View',
  'CCTV Footage', 'Drone Aerial', 'Underwater', 'Cargo/Hold',
  'Hull Damage', 'Machinery', 'Navigation Equipment', 'Other'
];

function MediaPreview({ item }) {
  const fileUrl = `${API}/evidence/file/${item.investigation_id}/${item.stored_name}`;

  if (item.file_category === 'image') {
    return <img src={fileUrl} alt={item.file_name} className="w-full h-44 object-cover" crossOrigin="use-credentials" />;
  }
  if (item.file_category === 'video') {
    return (
      <div className="relative w-full h-44 bg-black flex items-center justify-center group">
        <video className="w-full h-full object-cover" crossOrigin="use-credentials" preload="metadata">
          <source src={fileUrl} />
        </video>
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/50 transition-colors">
          <Play size={36} weight="fill" className="text-white/90" />
        </div>
      </div>
    );
  }
  if (item.file_category === 'audio') {
    return (
      <div className="w-full h-44 bg-gradient-to-br from-slate-800 to-slate-900 flex flex-col items-center justify-center p-4">
        <Microphone size={28} className="text-amber-400 mb-3" />
        <audio controls className="w-full" crossOrigin="use-credentials" style={{ height: 32 }}>
          <source src={fileUrl} />
        </audio>
      </div>
    );
  }
  const Icon = CATEGORY_ICONS[item.file_category] || FileText;
  return (
    <div className="w-full h-44 bg-slate-100 flex flex-col items-center justify-center">
      <Icon size={36} className="text-slate-400 mb-2" />
      <span className="text-xs text-slate-500">{item.file_ext}</span>
    </div>
  );
}

export function MediaDocumentation({ investigationId, canEdit }) {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [categoryTag, setCategoryTag] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [previewItem, setPreviewItem] = useState(null);

  const loadEvidence = useCallback(async () => {
    if (!investigationId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/evidence/investigation/${investigationId}`, { withCredentials: true });
      // Filter to only photo/video/audio
      setEvidence((data || []).filter(e => ['image', 'video', 'audio'].includes(e.file_category)));
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, [investigationId]);

  useEffect(() => { loadEvidence(); }, [loadEvidence]);

  const onDrop = useCallback((accepted) => {
    // Filter to only media files
    const mediaExts = new Set([
      '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff', '.svg',
      '.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv', '.webm', '.m4v',
      '.mp3', '.wav', '.aac', '.ogg', '.flac', '.m4a', '.wma',
    ]);
    const filtered = accepted.filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      return mediaExts.has(ext);
    });
    if (filtered.length < accepted.length) {
      alert(`${accepted.length - filtered.length} file(s) skipped — only images, videos, and audio are allowed here.`);
    }
    setSelectedFiles(filtered);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: true, maxFiles: 10 });

  const uploadFile = async (file) => {
    setUploadFileName(file.name);
    setUploadProgress(0);

    if (file.size <= CHUNK_SIZE) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('investigation_id', investigationId);
      formData.append('description', description);
      formData.append('category', categoryTag);

      await axios.post(`${API}/evidence/simple-upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
    } else {
      const initRes = await axios.post(`${API}/evidence/init-upload`, {
        file_name: file.name,
        file_size: file.size,
        investigation_id: investigationId,
        description,
        category: categoryTag,
      }, { withCredentials: true });

      const { upload_id, total_chunks } = initRes.data;

      for (let i = 0; i < total_chunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const chunkForm = new FormData();
        chunkForm.append('chunk', chunk);

        await axios.post(`${API}/evidence/upload-chunk/${upload_id}/${i}`, chunkForm, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploadProgress(Math.round(((i + 1) / total_chunks) * 100));
      }

      await axios.post(`${API}/evidence/complete-upload/${upload_id}`, {}, { withCredentials: true });
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles.length || !investigationId) return;
    setUploading(true);
    try {
      for (const file of selectedFiles) {
        await uploadFile(file);
      }
      setSelectedFiles([]);
      setDescription('');
      setCategoryTag('');
      setUploadProgress(0);
      setUploadFileName('');
      loadEvidence();
    } catch (e) {
      alert(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this file permanently?')) return;
    try {
      await axios.delete(`${API}/evidence/${id}`, { withCredentials: true });
      setEvidence(prev => prev.filter(e => e.id !== id));
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = evidence.filter(e => {
    if (filterCategory !== 'all' && e.file_category !== filterCategory) return false;
    if (searchTerm && !e.file_name.toLowerCase().includes(searchTerm.toLowerCase()) && !e.description?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: evidence.length,
    images: evidence.filter(e => e.file_category === 'image').length,
    videos: evidence.filter(e => e.file_category === 'video').length,
    audio: evidence.filter(e => e.file_category === 'audio').length,
  };

  return (
    <div className="space-y-5" data-testid="media-documentation">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total Media', count: stats.total, icon: Camera, color: 'bg-slate-800 text-white' },
          { label: 'Photos', count: stats.images, icon: Image, color: 'bg-purple-50 text-purple-800 border border-purple-200' },
          { label: 'Videos', count: stats.videos, icon: VideoCamera, color: 'bg-red-50 text-red-800 border border-red-200' },
          { label: 'Audio', count: stats.audio, icon: Microphone, color: 'bg-amber-50 text-amber-800 border border-amber-200' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl p-3 text-center ${s.color}`} data-testid={`media-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <s.icon size={20} weight="duotone" className="mx-auto mb-1 opacity-70" />
            <div className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>{s.count}</div>
            <div className="text-[10px] uppercase tracking-wider opacity-80">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Upload Area */}
      {canEdit && (
        <div className="bg-white rounded-xl border-2 border-dashed border-slate-300 overflow-hidden" data-testid="media-upload-section">
          <div
            {...getRootProps()}
            className={`p-6 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'
            }`}
            data-testid="media-dropzone"
          >
            <input {...getInputProps()} data-testid="media-file-input" accept="image/*,video/*,audio/*" />
            <div className="flex items-center justify-center gap-3 mb-2">
              <Camera size={28} className="text-purple-500" />
              <VideoCamera size={28} className="text-red-500" />
              <Microphone size={28} className="text-amber-500" />
            </div>
            {selectedFiles.length > 0 ? (
              <div>
                <p className="text-blue-700 font-semibold text-sm">{selectedFiles.length} file(s) selected</p>
                <p className="text-xs text-slate-500 mt-0.5">{selectedFiles.map(f => f.name).join(', ')}</p>
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-700 font-medium">{isDragActive ? 'Drop media files here...' : 'Drag & drop photos, videos, or audio'}</p>
                <p className="text-xs text-slate-400 mt-0.5">or click to browse. Large files supported via chunked upload.</p>
              </div>
            )}
          </div>

          {selectedFiles.length > 0 && (
            <div className="px-5 pb-5 pt-0 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-slate-600">Description</Label>
                  <Input value={description} onChange={e => setDescription(e.target.value)} className="mt-1 rounded-lg" placeholder="e.g., Wreckage overview from north..." data-testid="media-description" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-600">Documentation Tag</Label>
                  <Select value={categoryTag} onValueChange={setCategoryTag}>
                    <SelectTrigger className="mt-1 rounded-lg" data-testid="media-tag-select">
                      <SelectValue placeholder="Select tag..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DOC_TAGS.map(tag => (
                        <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg"
                  onClick={handleUpload}
                  disabled={uploading}
                  data-testid="media-upload-btn"
                >
                  <UploadSimple size={16} className="mr-2" />
                  {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
                </Button>
                <Button variant="outline" className="rounded-lg" onClick={() => setSelectedFiles([])} data-testid="media-clear-btn">
                  <X size={14} className="mr-1" /> Clear
                </Button>
              </div>
              {uploading && (
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-slate-600 truncate max-w-[200px]">{uploadFileName}</span>
                    <span className="text-slate-500">{Math.round(uploadProgress)}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 rounded-lg text-sm" placeholder="Search files..." data-testid="media-search" />
        </div>
        <div className="flex gap-1">
          {['all', 'image', 'video', 'audio'].map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filterCategory === cat ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`media-filter-${cat}`}
            >
              {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="text-center py-8 text-slate-400">Loading media files...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-10 text-center">
          <Camera size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">{evidence.length === 0 ? 'No media uploaded yet for this investigation' : 'No files match your filter'}</p>
          {evidence.length === 0 && canEdit && (
            <p className="text-xs text-slate-400 mt-1">Use the upload area above to add photos, videos, or audio recordings.</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="media-grid">
          {filtered.map(item => (
            <div key={item.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow group" data-testid={`media-card-${item.id}`}>
              <div className="relative cursor-pointer" onClick={() => setPreviewItem(item)}>
                <MediaPreview item={item} />
                <div className="absolute top-2 left-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${CATEGORY_COLORS[item.file_category] || 'bg-slate-100 text-slate-600'}`}>
                    {item.file_category.toUpperCase()}
                  </span>
                </div>
                {item.category_tag && (
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/90 text-slate-700 shadow-sm">{item.category_tag}</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <Eye size={24} className="text-white drop-shadow-lg" />
                </div>
              </div>
              <div className="p-3">
                <h3 className="text-sm font-medium text-slate-800 truncate" title={item.file_name}>{item.file_name}</h3>
                {item.description && <p className="text-xs text-slate-500 truncate mt-0.5">{item.description}</p>}
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] text-slate-400">{item.file_size_display} | {new Date(item.uploaded_at).toLocaleDateString()}</span>
                  <div className="flex gap-1">
                    <a href={`${API}/evidence/file/${item.investigation_id}/${item.stored_name}`} download className="p-1 rounded hover:bg-slate-100 text-slate-500" data-testid={`media-download-${item.id}`}>
                      <DownloadSimple size={14} />
                    </a>
                    {canEdit && (
                      <button onClick={() => handleDelete(item.id)} className="p-1 rounded hover:bg-red-50 text-red-400" data-testid={`media-delete-${item.id}`}>
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-[10px] text-slate-400 mt-1">{item.uploaded_by_name || item.uploaded_by}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewItem} onOpenChange={(open) => { if (!open) setPreviewItem(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          {previewItem && (
            <div>
              <div className="bg-black flex items-center justify-center min-h-[300px]">
                {previewItem.file_category === 'image' && (
                  <img src={`${API}/evidence/file/${previewItem.investigation_id}/${previewItem.stored_name}`} alt={previewItem.file_name} className="max-w-full max-h-[70vh] object-contain" crossOrigin="use-credentials" />
                )}
                {previewItem.file_category === 'video' && (
                  <video controls autoPlay className="max-w-full max-h-[70vh]" crossOrigin="use-credentials">
                    <source src={`${API}/evidence/file/${previewItem.investigation_id}/${previewItem.stored_name}`} />
                  </video>
                )}
                {previewItem.file_category === 'audio' && (
                  <div className="p-8 text-center">
                    <Microphone size={48} className="text-amber-400 mx-auto mb-4" />
                    <audio controls autoPlay className="w-full max-w-md" crossOrigin="use-credentials">
                      <source src={`${API}/evidence/file/${previewItem.investigation_id}/${previewItem.stored_name}`} />
                    </audio>
                  </div>
                )}
              </div>
              <div className="p-4 bg-white space-y-2">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[previewItem.file_category] || ''}`}>{previewItem.file_category}</span>
                  {previewItem.category_tag && <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">{previewItem.category_tag}</span>}
                </div>
                <h3 className="text-base font-semibold text-slate-900">{previewItem.file_name}</h3>
                {previewItem.description && <p className="text-sm text-slate-600">{previewItem.description}</p>}
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>{previewItem.file_size_display}</span>
                  <span>Uploaded: {new Date(previewItem.uploaded_at).toLocaleString()}</span>
                  <span>By: {previewItem.uploaded_by_name || previewItem.uploaded_by}</span>
                </div>
                <div className="flex gap-2 pt-2">
                  <a href={`${API}/evidence/file/${previewItem.investigation_id}/${previewItem.stored_name}`} download>
                    <Button variant="outline" className="rounded-lg text-sm" data-testid="preview-download">
                      <DownloadSimple size={16} className="mr-2" /> Download
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
